import { Keplr, Window as KeplrWindow } from "@keplr-wallet/types";
import { Eip1193Provider } from "ethers";
import {
  Address,
  gasToken,
  Msg,
  Network,
  Signer,
  Simulation,
  TxResult,
} from "rujira.js";
import { CosmosAdapter } from "./cosmos";
import { Eip1193Adapter } from "./eip1193";
import { Eip6963Adapter } from "./eip6963";
import { walletType } from "./utils";
import {
  BitcoinProvider,
  UtxoNetworks,
  utxoNetworkTochain,
  UtxoQueryClient,
} from "./utxo";

interface XfiWindow extends UtxoNetworks {
  binance: unknown;
  ethereum: Eip1193Provider & { isXDEFI: true };
  keplr: Keplr & { isXDEFI: true };
}

declare global {
  interface Window extends KeplrWindow {
    xfi?: XfiWindow;
  }
}

export class CtrlAdapter implements Signer {
  constructor(
    private e: Eip1193Adapter,
    private c: CosmosAdapter,
    private b?: XfiWindow
  ) {}
  async connect(): Promise<Address[]> {
    if (!this.b) throw new Error("Ctrl extension not found");
    const x = await Promise.all([
      this.e.connect(),
      this.c.connect(),
      // WASA WASA WASA WASSUP
      this.bitconnect(this.b.bitcoin),
      this.bitconnect(this.b.bitcoincash),
      this.bitconnect(this.b.dogecoin),
      this.bitconnect(this.b.litecoin),
    ]);
    return x.flat();
  }
  async simulate(msg: Msg): Promise<Simulation> {
    const type = walletType(msg.account.address);
    switch (type) {
      case "cosmos":
        return this.c.simulate(msg);
      case "evm":
        return this.e.simulate(msg);
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by CTRL Wallet`);
      default:
        const client = new UtxoQueryClient(type[1], msg.account.address);
        const utxos = await client.fetch();
        const psbt = await msg.toPsbt(utxos);
        return {
          ...gasToken(msg.account.network),
          amount: psbt.fee,
          gas: 0n,
        };
    }
  }
  async signAndBroadcast(simulation: Simulation, msg: Msg): Promise<TxResult> {
    const type = walletType(msg.account.address);
    switch (type) {
      case "cosmos":
        return this.c.signAndBroadcast(simulation, msg);
      case "evm":
        return this.e.signAndBroadcast(simulation, msg);
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by CTRL Wallet`);

      default:
        if (!this.b) throw new Error("Ctrl extension not found");
        const key = utxoNetworkTochain(msg.account.network);
        const client = new UtxoQueryClient(type[1], msg.account.address);
        const utxos = await client.fetch();
        const psbt = await msg.toPsbt(utxos);
        const { amount, memo, recipient } = psbt;
        const inboundAddress = await msg.inboundAddress?.(msg.account.network);

        return new Promise((resolve, reject) => {
          if (!this.b) {
            reject(new Error("Ctrl extension not found"));
            return;
          }

          const params = [
            {
              from: msg.account.address,
              feeRate: Number(inboundAddress?.gasRate),
              recipient,
              amount: { amount: Number(amount), decimals: 0 },
              memo,
            },
          ];

          this.b[key].request(
            {
              method: "transfer",
              params,
            },
            (error, result) => {
              if (error) reject(error);
              resolve({
                network: msg.account.network,
                address: msg.account.address,
                txHash: result,
              });
            }
          );
        });
    }
  }
  onChange?: ((cb: () => void) => void) | undefined;
  isAvailable() {
    return this.e.isAvailable();
  }

  bitconnect(provider: BitcoinProvider): Promise<Address[]> {
    return new Promise<string[]>((resolve, reject) =>
      provider.request(
        { method: "request_accounts", params: [] },
        (error: any, accounts: string[]) => {
          if (error) reject(error);
          resolve(accounts);
        }
      )
    );
  }

  public networks(): Network[] {
    return [
      "BCH",
      "BTC",
      "DOGE",
      "LTC",
      ...this.c.networks(),
      ...this.e.networks(),
    ];
  }
}

const provider = new CtrlAdapter(
  new Eip6963Adapter("io.xdefi"),
  new CosmosAdapter(() => window.xfi?.keplr),
  window.xfi
);

export default provider;
